import { ActivatedRouteSnapshot } from '@angular/router';
import { Submission } from '@submissions/next/models';
import { of } from 'rxjs';
import { EntityTypeExtend } from '@data-access/submissions';
import { organizationContactTokenizer } from '../store/tokens/organization-contact.token';

export const organizationContactResolver = (route: ActivatedRouteSnapshot) => {
  const organizationContactsStore = organizationContactTokenizer();
  const entityType: EntityTypeExtend = route.data['entityType'] ?? 'workspace';

  // Common function to setup context and load data
  const setupAndLoad = (
    entityId: string,
    entityType: EntityTypeExtend,
    options?: { relationshipId?: string; studyId?: string },
  ) => {
    if (!entityId || !entityType) return of(false);

    organizationContactsStore.setupContext(entityId, entityType, options);
    organizationContactsStore.loadOrganizationRoles();
    organizationContactsStore.loadOrganizationContact();

    return of(true);
  };

  // Determine entity type and load corresponding data
  switch (entityType) {
    // Default to 'workspace' if entity type is not specified
    case 'workspace': {
      const submission = route.parent?.data['submission'] as
        | Submission
        | undefined;
      if (!submission?.id) {
        return of(false);
      }
      const relationshipIdRaw =
        submission.data?.['organization_affiliation_relationship_id'];
      const relationshipId =
        typeof relationshipIdRaw === 'string' && relationshipIdRaw.length > 0
          ? relationshipIdRaw
          : undefined;
      return setupAndLoad(submission.id, entityType, { relationshipId });
    }

    // Load data for 'study' entity type
    // Use the study already resolved by the parent studyResolver to avoid a redundant HTTP call.
    case 'study': {
      const studyDetails = route.parent?.data['study'];
      const id = studyDetails?.id ?? '';
      if (!id) {
        console.error(
          'organizationContactResolver: Missing study id from parent route data',
        );
        return of(null);
      }
      return setupAndLoad(id, entityType);
    }

    // Load data for 'site' entity type
    // Use the site already resolved by the parent siteResolver to avoid a redundant HTTP call.
    case 'site': {
      const siteDetails = route.parent?.data['site'];
      const id = siteDetails?.id ?? '';
      if (!id) {
        console.error(
          'organizationContactResolver: Missing site id from parent route data',
        );
        return of(null);
      }
      const siteStudyId = siteDetails?.study_id ?? '';
      return setupAndLoad(id, entityType, { studyId: siteStudyId });
    }

    default:
      return of(false);
  }
};
